import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

/** Canonical store URLs for the Helvetic Padel mobile app (QR / smart landing). */
export const APP_STORE_URL = 'https://apps.apple.com/ch/app/helvetic-padel/id6775912874';
/** Opens the App Store *app* — safer auto-open from Safari than https://apps.apple.com */
export const APP_STORE_ITMS_URL = 'itms-apps://apps.apple.com/app/id6775912874';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ch.helveticpadel.mobile';

const GOOGLE_PLAY_ICON = '/images/google-play-store-icon.svg';
const APPLE_STORE_ICON = '/images/apple-app-store-icon.svg';

function detectAndroidPlayStore() {
  if (typeof navigator === 'undefined') return null;
  if (/Android/i.test(navigator.userAgent || '')) return PLAY_STORE_URL;
  return null;
}

function isAppleMobile() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && Number(navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

/**
 * Try to hand off to the App Store app without navigating Safari to apps.apple.com
 * (https redirects from QR often show “Safari cannot open the page”).
 */
function tryOpenAppStoreApp() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'display:none;width:0;height:0;border:0;';
    iframe.src = APP_STORE_ITMS_URL;
    document.body.appendChild(iframe);
    window.setTimeout(() => {
      try {
        iframe.remove();
      } catch (_) {
        /* ignore */
      }
    }, 2500);
  } catch (_) {
    /* ignore */
  }
  // Top-level itms navigation — does not leave a broken https URL in the address bar
  window.location.href = APP_STORE_ITMS_URL;
}

const Shell = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem 3rem;
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(4, 54, 99, 0.12), transparent),
    linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%);
`;

const Card = styled.div`
  width: 100%;
  max-width: 28rem;
  text-align: center;
`;

const Brand = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #043663;
`;

const Title = styled.h1`
  margin: 0 0 0.65rem;
  font-size: clamp(1.65rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
`;

const Lead = styled.p`
  margin: 0 0 1.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #64748b;
`;

const Status = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #043663;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
`;

const BadgeLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: min(240px, 85vw);
  min-height: 64px;
  padding: 0.65rem 1.1rem 0.65rem 0.85rem;
  box-sizing: border-box;
  background: #000;
  color: #fff;
  border-radius: 10px;
  text-decoration: none;
  text-align: left;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.92;
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #043663;
    outline-offset: 4px;
  }
`;

const IconWrap = styled.span`
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
`;

const LabelStack = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.1rem;
  min-width: 0;
`;

const Eyebrow = styled.span`
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  line-height: 1.1;
  color: #fff;
  opacity: 0.95;
`;

const StoreName = styled.span`
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
`;

const Hint = styled.p`
  margin: 1.5rem 0 0;
  font-size: 0.8rem;
  color: #94a3b8;
`;

const HomeLink = styled(Link)`
  display: inline-block;
  margin-top: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #d52b1e;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function StoreBadge({ href, iconSrc, iconAlt, eyebrow, name }) {
  return (
    <BadgeLink href={href} rel="noopener noreferrer">
      <IconWrap>
        <img src={iconSrc} alt={iconAlt} width={36} height={36} />
      </IconWrap>
      <LabelStack>
        <Eyebrow>{eyebrow}</Eyebrow>
        <StoreName>{name}</StoreName>
      </LabelStack>
    </BadgeLink>
  );
}

/**
 * Public smart landing for a static QR code.
 * Android → Play Store https. iPhone → itms-apps:// (App Store app), badges stay as fallback.
 * Add ?noredirect=1 to skip auto-open while testing.
 */
export default function GetAppPage() {
  const [searchParams] = useSearchParams();
  const skipRedirect = searchParams.get('noredirect') === '1';
  const [redirecting, setRedirecting] = useState(false);
  const onIphone = isAppleMobile();

  useSEO({
    title: 'Download the Helvetic Padel app',
    description: 'Get the Helvetic Padel Series app on the App Store or Google Play.',
    canonical: 'https://helveticpadelseries.ch/get-app',
  });

  useEffect(() => {
    if (skipRedirect) return undefined;

    const play = detectAndroidPlayStore();
    if (play) {
      setRedirecting(true);
      window.location.replace(play);
      return undefined;
    }

    if (isAppleMobile()) {
      setRedirecting(true);
      // Defer one tick so the page (and badges) paint before the App Store handoff
      const t = window.setTimeout(() => {
        tryOpenAppStoreApp();
      }, 100);
      return () => window.clearTimeout(t);
    }

    return undefined;
  }, [skipRedirect]);

  return (
    <Shell>
      <Card>
        <Brand>Helvetic Padel Series</Brand>
        <Title>Get the app</Title>
        <Lead>
          {onIphone
            ? 'Opening the App Store… If nothing happens, tap App Store below.'
            : 'Choose your store — on Android we may open Google Play automatically.'}
        </Lead>
        {redirecting ? (
          <Status>
            {onIphone
              ? 'Opening App Store… If you stay on this page, tap the App Store badge.'
              : 'Opening Google Play… If nothing happens, tap a badge below.'}
          </Status>
        ) : null}
        <Actions>
          <StoreBadge
            href={PLAY_STORE_URL}
            iconSrc={GOOGLE_PLAY_ICON}
            iconAlt=""
            eyebrow="GET IT ON"
            name="Google Play"
          />
          <StoreBadge
            href={APP_STORE_URL}
            iconSrc={APPLE_STORE_ICON}
            iconAlt=""
            eyebrow="Download on the"
            name="App Store"
          />
        </Actions>
        <Hint>
          {onIphone
            ? 'We open the App Store app directly. The badge still works if iOS blocks the auto-open.'
            : 'On Android we try to open Google Play automatically. You can always use the badges if needed.'}
        </Hint>
        <HomeLink to="/">Back to website</HomeLink>
      </Card>
    </Shell>
  );
}
